import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography
} from "@mui/material";

import { CONVERSATION_LENS } from "../../../../utils/terminology";
import type { LensProtocol } from "../../../../data/hooks/useLensProtocols";
import { useWizardStore } from "../../store/wizardStore";

interface SelectLensProps {
  protocols: LensProtocol[];
  isLoadingProtocols: boolean;
  isCheckingData: boolean;
  showNoDataWarning: boolean;
}

const SelectLens = ({
  protocols,
  isLoadingProtocols,
  isCheckingData,
  showNoDataWarning
}: SelectLensProps) => {
  const { selectedLens, setSelectedLens } = useWizardStore();

  const handleLensChange = (event: SelectChangeEvent<string>) => {
    const selectedName = event.target.value;
    const selectedProtocol = protocols.find(p => p.name === selectedName);
    if (selectedProtocol) {
      setSelectedLens(selectedProtocol.name, selectedProtocol.fullPath);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        {CONVERSATION_LENS}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select the {CONVERSATION_LENS.toLowerCase()} to analyze performance against
      </Typography>
      <FormControl fullWidth>
        <Select
          value={selectedLens}
          onChange={handleLensChange}
          displayEmpty
          disabled={isLoadingProtocols}
          startAdornment={isLoadingProtocols ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
        >
          <MenuItem value="" disabled>
            Select a {CONVERSATION_LENS.toLowerCase()}
          </MenuItem>
          {protocols.map((protocol) => (
            <MenuItem key={protocol.fullPath} value={protocol.name}>
              {protocol.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {showNoDataWarning && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            No coaching data available for the selected user, week, and {CONVERSATION_LENS.toLowerCase()} combination.
            Please try a different selection.
          </Typography>
        </Alert>
      )}

      {isCheckingData && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Checking data availability...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SelectLens;
